import { useState, useCallback, useRef } from 'react';

  /**
   * Hook to manage deploy targeting animations
   * Tracks active animations and prevents duplicates
   * Uses instanceId to ensure each deployment instance only plays animation once
   */
  export const useDeployTargetAnimations = () => {
    const [activeAnimations, setActiveAnimations] = useState(new Map());
    const completedAnimationsRef = useRef(new Set()); // Track completed animations by instanceId

    /**
     * Register a new deploy targeting animation
     * @param {Object} animationData - { fromSlot, toSlot, animationType, deployUnitId, deployUnitName, deployUnitInstanceId, animationKey }
     */
    const registerAnimation = useCallback((animationData) => {
      if (!animationData || !animationData.fromSlot || !animationData.toSlot) {
        console.warn('⚠️ useDeployTargetAnimations: Invalid animation data', animationData);
        return;
      }

      // ✅ FIX: Use instanceId-based key to ensure each deployment instance only plays once
      // If backend provided animationKey, use it; otherwise create one
      const animationKey = animationData.animationKey || 
        `${animationData.deployUnitInstanceId || 'unknown'}-${animationData.fromSlot.zone}-${animationData.fromSlot.index}-${animationData.toSlot.zone}-${animationData.toSlot.index}-${animationData.animationType}`;

      // ✅ FIX: Also check by instanceId to prevent same deployment from animating multiple times
      // Include playerId in key to differentiate between player and opponent generals
      const instanceIdKey = animationData.deployUnitInstanceId 
        ? `instance-${animationData.deployUnitInstanceId}-${animationData.toSlot.zone}-${animationData.toSlot.index !== null ? animationData.toSlot.index : 'general'}-${animationData.toSlot.playerId || 'unknown'}`
        : null;

      // ✅ FIX: Use functional update to check current state (avoid stale closure)
      setActiveAnimations(prev => {
        // Prevent duplicates - check by animationKey
        if (prev.has(animationKey) || completedAnimationsRef.current.has(animationKey)) {
          console.log('⚠️ useDeployTargetAnimations: Animation already active or completed (by key)', animationKey);
          return prev; // Don't modify state
        }
        
        // ✅ FIX: Also check by instanceId to prevent same deployment from animating multiple times
        // Include playerId in key to differentiate between player and opponent generals
        if (instanceIdKey) {
          const existingByInstanceId = Array.from(prev.values()).find(anim => {
            const animInstanceKey = anim.deployUnitInstanceId 
              ? `instance-${anim.deployUnitInstanceId}-${anim.toSlot.zone}-${anim.toSlot.index !== null ? anim.toSlot.index : 'general'}-${anim.toSlot.playerId || 'unknown'}`
              : null;
            return animInstanceKey === instanceIdKey;
          });
          
          if (existingByInstanceId) {
            console.log('⚠️ useDeployTargetAnimations: Animation already active for this deployment instance', instanceIdKey);
            return prev; // Don't modify state
          }
          
          // Check completed animations by instanceId
          if (completedAnimationsRef.current.has(instanceIdKey)) {
            console.log('⚠️ useDeployTargetAnimations: Animation already completed for this deployment instance', instanceIdKey);
            return prev; // Don't modify state
          }
        }

        console.log(`✅ useDeployTargetAnimations: Registering animation`, animationKey, { instanceId: animationData.deployUnitInstanceId, instanceIdKey });

        const next = new Map(prev);
        next.set(animationKey, {
          ...animationData,
          key: animationKey,
          instanceIdKey, // Store for completion tracking
          registeredAt: Date.now()
        });
        return next;
      });
    }, []); // ✅ FIX: No dependencies - use functional updates

  /**
   * Complete an animation (remove from active)
   * @param {string} animationKey - Unique animation key
   */
  const completeAnimation = useCallback((animationKey) => {
    console.log(`✅ useDeployTargetAnimations: Completing animation`, animationKey);
    
    setActiveAnimations(prev => {
      const animation = prev.get(animationKey);
      const next = new Map(prev);
      next.delete(animationKey);
      
      // ✅ FIX: Mark as completed by instanceId to prevent same deployment from animating again
      if (animation && animation.instanceIdKey) {
        completedAnimationsRef.current.add(animation.instanceIdKey);
        // Keep in completed set permanently (until component unmounts) to prevent re-animation
        console.log(`✅ useDeployTargetAnimations: Marked instanceId animation as completed (permanent)`, animation.instanceIdKey);
      }
      
      // Also mark by animationKey for short-term prevention
      completedAnimationsRef.current.add(animationKey);
      setTimeout(() => {
        completedAnimationsRef.current.delete(animationKey);
      }, 100); // Clear animationKey after 100ms, but keep instanceIdKey permanently
      
      return next;
    });
  }, []);

  return {
    activeAnimations: Array.from(activeAnimations.values()),
    registerAnimation,
    completeAnimation
  };
};

